import { useEffect, useState } from "react";
import RangosService from "../api/rangos.api";
import DeliveryService from "../../../services/delivery.service";

/**
 * Hook para resolver los datos que necesita RangoFormPage: la lista de
 * transportistas (DeliveryService.getTransports(), legacy compartido) y,
 * en modo edición, el rango a editar.
 *
 * No hay GET por id en rangos.api.js (el contrato solo tiene list/create/
 * update/delete, ver docs/api/rangos.md): si `rangoDesdeState` llega (la
 * navegación viene del listado, con la fila en location.state.rango) se usa
 * tal cual; si no (acceso directo a la URL o F5), se recupera con
 * RangosService.list() y se busca por id.
 *
 * @param {string|undefined} id - id de la URL (undefined en alta)
 * @param {Object|null} rangoDesdeState - Fila recibida por navegación (location.state.rango), o null
 * @returns {{
 *   transportistas: Array,
 *   rango: Object|null,
 *   cargando: boolean,
 *   error: string|null
 * }} Datos resueltos para RangoFormPage
 */
export const useRangoFormData = (id, rangoDesdeState) => {
    const esEdicion = Boolean(id);

    const [transportistas, setTransportistas] = useState([]);
    const [rango, setRango] = useState(rangoDesdeState);
    // DECISION: solo se bloquea la UI al EDITAR. Crear un rango solo necesita el
    // catalogo de transportistas, asi que el formulario aparece de inmediato y el
    // catalogo llega detras; editar si carga el registro y justifica el loader.
    // Patron del proyecto: reponedores/NuevaAsignacion y traspasos/TraspasoAlmacenes.
    const [cargando, setCargando] = useState(esEdicion);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelado = false;

        (async () => {
            setCargando(esEdicion);
            setError(null);
            try {
                const buscarRango = esEdicion && !rangoDesdeState
                    ? RangosService.list().then((rangos) => rangos.find((r) => String(r.id) === String(id)) ?? null)
                    : Promise.resolve(rangoDesdeState);

                const [listaTransportistas, rangoResuelto] = await Promise.all([
                    DeliveryService.getTransports(),
                    buscarRango,
                ]);
                if (cancelado) return;

                setTransportistas(listaTransportistas);
                if (esEdicion && !rangoResuelto) {
                    setError("El rango de precios solicitado no existe o ya fue eliminado.");
                } else {
                    setRango(rangoResuelto);
                }
            } catch (err) {
                if (!cancelado) setError(err.message || "No se pudo cargar la información necesaria.");
            } finally {
                if (!cancelado) setCargando(false);
            }
        })();

        return () => { cancelado = true; };
    }, [id, esEdicion, rangoDesdeState]);

    return { transportistas, rango, cargando, error };
};
